import { BaseQueryParams } from "../baseQuery";
import { WIZARD_TIMEOUT_MS } from "../../utils/wizard";

export const wizardCreateService = BaseQueryParams(
  "wizardService",
  [
    "SELECT_ACCOUNT_USER",
    "MAPPING",
    "Select_Source",
    "MIGRATION_SESSION",
    "CHANGE_MIGRATION",
    "SURVEY",
    "DETAILS_MIGRATION",
    "RATE",
    "CONTINUE_MIGRATION",
  ],
  { timeout: WIZARD_TIMEOUT_MS },
).injectEndpoints({
  endpoints: (builder) => ({
    getMigrationSession: builder.query({
      query: () => ({
        url: "/migrations/wizard/migration",
        method: "GET",
      }),

      providesTags: () => ["MIGRATION_SESSION"],
    }),

    getMigrationProgress: builder.query({
      query: ({ migration_id }) => ({
        url: `/migrations/${migration_id}/details/progress`,
        method: "GET",
      }),
    }),

    getNewMigrationSession: builder.query({
      query: () => ({
        url: "/migrations/wizard/migration",
        method: "GET",
      }),
    }),

    getMigrationDetails: builder.query({
      query: ({ migration_id }) => ({
        url: `/migrations/${migration_id}/details`,
        method: "GET",
      }),

      providesTags: () => ["DETAILS_MIGRATION"],
    }),

    createMigration: builder.mutation({
      query: () => ({
        url: "/migrations/wizard/migration",
        method: "POST",
      }),
    }),

    continueMigrationSession: builder.mutation({
      query: ({ migrationId, tryStep }) => ({
        url: `/migrations/wizard/migration/${migrationId}`,
        method: "PUT",
        body: { reset: 0, try_step: tryStep },
      }),

      // invalidatesTags: () => ["CONTINUE_MIGRATION"],
      invalidatesTags: () => ["MIGRATION_SESSION"],
    }),

    editMigrationSession: builder.mutation({
      query: (migrationId) => ({
        url: `/migrations/wizard/migration/${migrationId}`,
        method: "PUT",
        body: { reset: 1 },
      }),

      invalidatesTags: () => ["MIGRATION_SESSION"],
    }),

    createSelectAccountUser: builder.mutation({
      query: (body) => ({
        url: "/profile/loyalty/users",
        method: "POST",
        body,
      }),

      providesTags: () => ["SELECT_ACCOUNT_USER"],
    }),

    getEmails: builder.query({
      query: ({ email }) => ({
        url: `/migrations/wizard/get-accounts${
          email ? `?search=${encodeURIComponent(email)}` : ""
        }`,
        method: "GET",
      }),

      keepUnusedDataFor: 0,
      invalidatesTags: () => ["SELECT_ACCOUNT_USER"],
    }),

    applyMigrationToAccount: builder.mutation({
      query: (account_email) => ({
        url: "/migrations/wizard/account-selection",
        method: "POST",
        body: { account_email },
      }),
    }),

    getAvailableStores: builder.query({
      query: () => ({
        url: "/migrations/wizard/available-stores",
        method: "GET",
      }),

      providesTags: () => ["Select_Source"],
    }),

    setupStores: builder.mutation({
      query: (body) => ({
        url: "/migrations/wizard/stores-setup",
        method: "POST",
        body,
      }),

      invalidatesTags: () => ["MAPPING"],
    }),

    checkTypeConnect: builder.mutation({
      query: (body) => ({
        url: "/migrations/wizard/cart-fields",
        method: "POST",
        body,
      }),

      invalidatesTags: (result, error, body) => {
        if (body.reloadNotice) {
          return ["MIGRATION_SESSION", "NOTICE", "MAPPING_VIEW"];
        }
        return ["MIGRATION_SESSION"];
      },
    }),

    getDownloadBridge: builder.query({
      query: () => ({
        url: "/migrations/wizard/migration/bridge",
        method: "GET",
      }),
    }),

    // Connects method //

    connectionStore: builder.mutation({
      query: ({ body, type, connectionType, id }) => ({
        url: `/migrations/wizard/migration/store/${type}/connection/${connectionType}${
          id ? `/${id}` : ""
        }`,
        method: "POST",
        body,
      }),

      invalidatesTags: () => ["MIGRATION_SESSION"],
    }),

    // Connects method //

    // SETTINGS //
    getEntities: builder.query({
      query: ({ id }) => ({
        url: `/migrations/${id}/wizard/entities-selection`,
        method: "GET",
      }),

      keepUnusedDataFor: 0,
      invalidatesTags: () => ["MIGRATION_SESSION"],
    }),

    getSmartUpdateEntities: builder.query({
      query: ({ id }) => ({
        url: `/migrations/${id}/wizard/get-smart-update-entities`,
        method: "GET",
      }),

      keepUnusedDataFor: 0,
    }),

    getSmartUpdateSelectedEntities: builder.query({
      query: ({ id }) => ({
        url: `/migrations/${id}/wizard/get-smart-update-selected-entities`,
        method: "GET",
      }),

      providesTags: () => ["SELECTED_ENTITIES"],
      keepUnusedDataFor: 0,
    }),

    getSmartUpdateCount: builder.query({
      query: ({ id }) => ({
        url: `/migrations/${id}/wizard/get-smart-update-counts`,
        method: "GET",
      }),
    }),

    requiredPlugins: builder.mutation({
      query: ({ body, id }) => ({
        url: `/migrations/wizard/check-required-plugins/${id}`,
        method: "POST",
        body,
      }),
    }),

    setEntities: builder.mutation({
      query: (body) => ({
        url: "/migrations/wizard/options",
        method: "POST",
        body,
      }),

      invalidatesTags: () => ["RESOURCES"],
    }),

    setSmartUpdateEntities: builder.mutation({
      query: ({ id, body }) => ({
        url: `/migrations/${id}/wizard/save-entities-for-smart-update`,
        method: "POST",
        body,
      }),

      invalidatesTags: () => ["SELECTED_ENTITIES", "DETAILS_MIGRATION"],
    }),

    getOptions: builder.mutation({
      query: (body) => ({
        url: "/migrations/wizard/options",
        method: "POST",
        body,
      }),
    }),

    setOptions: builder.mutation({
      query: (body) => ({
        url: "/migrations/wizard/options",
        method: "POST",
        body,
      }),
    }),

    getMappings: builder.query({
      query: ({ id, types, reload }) => ({
        url: `/migrations/${id}/wizard/mapping${
          reload ? `?reload=${reload}` : ""
        }${types ? `&${types}` : ""}`,
        method: "GET",
      }),

      providesTags: () => ["MAPPINGS"],
    }),

    setMappings: builder.mutation({
      query: ({ id, body }) => ({
        url: `/migrations/${id}/wizard/mapping`,
        method: "POST",
        body,
      }),

      invalidatesTags: (result, error, { skipReload }) =>
        skipReload ? [] : ["MAPPINGS"],
    }),

    getNoticesByStep: builder.query({
      query: ({ step }) => ({
        url: `/migrations/wizard/notices${step ? `/${step}` : ""}`,
        method: "GET",
      }),

      providesTags: () => ["NOTICE"],
    }),

    // DEMO
    startDemo: builder.mutation({
      query: (body) => ({
        url: "/migrations/wizard/migration/demo",
        method: "POST",
        body,
      }),
    }),

    getDemoProgressEntities: builder.query({
      query: () => ({
        url: "/migrations/wizard/migration/demo/entities",
        method: "GET",
      }),
    }),

    getSurvey: builder.query({
      query: () => ({
        url: "/migrations/wizard/survey",
        method: "GET",
      }),

      providesTags: () => ["SURVEY"],
    }),

    setSurvey: builder.mutation({
      query: (body) => ({
        url: "/migrations/wizard/survey",
        method: "POST",
        body,
      }),

      invalidatesTags: () => ["SURVEY"],
    }),

    getDemoResult: builder.query({
      query: () => ({
        url: "/migrations/wizard/migration/demo/result",
        method: "GET",
      }),
    }),

    getDemoRate: builder.query({
      query: ({ id }) => ({
        url: `/migrations/wizard/demo/rate/${id}`,
        method: "GET",
      }),

      keepUnusedDataFor: 0,
    }),

    setDemoRate: builder.mutation({
      query: ({ id, body }) => ({
        url: `/migrations/wizard/demo/rate/${id}`,
        method: "POST",
        body,
      }),
    }),

    saveDemoEstimate: builder.mutation({
      query: ({ id, body }) => ({
        url: `/migrations/${id}/wizard/save-demo-estimate`,
        method: "POST",
        body,
      }),
    }),
    // DEMO

    // FULL
    getInsurances: builder.query({
      query: () => ({
        url: "/migrations/wizard/insurances",
        method: "GET",
      }),

      keepUnusedDataFor: 0,
    }),

    sendInsurances: builder.mutation({
      query: (body) => ({
        url: "/migrations/wizard/insurance",
        method: "POST",
        body,
      }),

      invalidatesTags: () => ["DETAILS_MIGRATION"],
    }),

    setOptionsFull: builder.mutation({
      query: (body) => ({
        url: "/migrations/wizard/options",
        method: "POST",
        body,
      }),

      invalidatesTags: () => ["DETAILS_MIGRATION"],
    }),

    sendEvents: builder.mutation({
      query: (body) => ({
        url: "/send-events",
        method: "POST",
        body,
      }),
    }),

    setCoupon: builder.mutation({
      query: (code) => ({
        url: "/migrations/wizard/apply-coupon",
        method: "POST",
        body: { discount_coupon_code: code },
      }),

      invalidatesTags: () => ["DETAILS_MIGRATION"],
    }),

    reapplyCoupon: builder.mutation({
      query: ({ id, code }) => ({
        url: `/migrations/${id}/remove-or-reapply-coupon`,
        method: "POST",
        body: { discount_coupon_code: code },
      }),

      invalidatesTags: () => ["DETAILS_MIGRATION"],
    }),

    startFullMigration: builder.mutation({
      query: () => ({
        url: "/migrations/wizard/start-full-migration",
        method: "POST",
        body: {
          rdb: 0,
        },
      }),

      invalidatesTags: () => ["DETAILS_MIGRATION"],
    }),

    validateCartPair: builder.mutation({
      query: (body) => ({
        url: "/migrations/wizard/cart-pairs/validate",
        method: "POST",
        body,
      }),
    }),

    calculatingRecentDataProcess: builder.mutation({
      query: () => ({
        url: "/migrations/wizard/check-calculating-recent-data-process",
        method: "POST",
      }),

      // invalidatesTags: () => ["MIGRATION_SESSION"],
    }),

    calculateMigrationData: builder.mutation({
      query: () => ({
        url: "/migrations/wizard/calculate-migration-data",
        method: "POST",
      }),

      invalidatesTags: () => ["DETAILS_MIGRATION"],
    }),

    // FULL

    // SETTINGS //

    getMigrationDetailsNotices: builder.mutation({
      query: (body) => ({
        url: "/migrations/notices",
        method: "POST",
        body,
      }),
    }),

    downloadResourceFile: builder.query({
      query: ({ id, body }) => ({
        url: `/migrations/${id}/target/details/download-resource-file`,
        method: "GET",
        params: body,
      }),

      keepUnusedDataFor: 0,
    }),

    remoteCreateMigrationLink: builder.query({
      query: ({ source, target }) => ({
        url: `/migrations/wizard/from-${source}/to-${target}/remote-create-migration-link`,
        method: "GET",
      }),
    }),

    reconfigureTargetStore: builder.mutation({
      query: ({ migration }) => ({
        url: `/migrations/wizard/reconfigure-target-store/${migration}`,
        method: "POST",
      }),
    }),

    getUploadResourcesList: builder.query({
      query: ({ migration, entityQuery }) => ({
        url: `/migrations/${migration}/details/upload-resources-list`,
        method: "GET",
      }),

      keepUnusedDataFor: 0,
      providesTags: () => ["RESOURCES"],
    }),

    uploadResourcesList: builder.mutation({
      query: ({ migration, body }) => ({
        url: `/migrations/${migration}/details/upload-source-resource`,
        method: "POST",
        body,
      }),

      invalidatesTags: () => ["RESOURCES"],
    }),

    downloadPlugins: builder.query({
      query: ({ direction, body }) => ({
        url: `/migrations/plugins/${direction}`,
        method: "GET",
        params: body,
      }),

      keepUnusedDataFor: 0,
    }),

    getMappingView: builder.query({
      query: ({ migration }) => ({
        url: `/migrations/${migration}/entities-fields-describe`,
        method: "GET",
      }),

      providesTags: () => ["MAPPING_VIEW"],
    }),
  }),
});

export const {
  useGetMigrationSessionQuery,
  useCreateMigrationMutation,
  useContinueMigrationSessionMutation,
  useGetEmailsQuery,
  useApplyMigrationToAccountMutation,
  useGetAvailableStoresQuery,
  useSetupStoresMutation,
  useCheckTypeConnectMutation,
  useConnectionStoreMutation,
  useGetEntitiesQuery,
  useSetEntitiesMutation,
  useGetNoticesByStepQuery,
  useLazyGetNoticesByStepQuery,
  useGetOptionsMutation,
  useSetOptionsMutation,
  useStartDemoMutation,
  useGetDemoProgressEntitiesQuery,
  useGetSurveyQuery,
  useSetSurveyMutation,
  useGetDemoResultQuery,
  useGetDemoRateQuery,
  useSetDemoRateMutation,
  useSaveDemoEstimateMutation,
  useGetInsurancesQuery,
  useSendInsurancesMutation,
  useSetOptionsFullMutation,
  useSendEventsMutation,
  useSetCouponMutation,
  useReapplyCouponMutation,
  useGetMigrationDetailsQuery,
  useStartFullMigrationMutation,
  useGetNewMigrationSessionQuery,
  useLazyGetNewMigrationSessionQuery,
  useValidateCartPairMutation,
  useEditMigrationSessionMutation,
  useCalculatingRecentDataProcessMutation,
  useCalculateMigrationDataMutation,
  useGetDownloadBridgeQuery,
  useGetMigrationProgressQuery,
  useRequiredPluginsMutation,
  useGetMigrationDetailsNoticesMutation,
  useLazyDownloadResourceFileQuery,
  useLazyRemoteCreateMigrationLinkQuery,
  useReconfigureTargetStoreMutation,
  useGetMappingsQuery,
  useLazyGetMappingsQuery,
  useSetMappingsMutation,
  useGetUploadResourcesListQuery,
  useLazyGetUploadResourcesListQuery,
  useUploadResourcesListMutation,
  useLazyDownloadPluginsQuery,
  useCreateSelectAccountUserMutation,
  useGetSmartUpdateEntitiesQuery,
  useGetSmartUpdateSelectedEntitiesQuery,
  useSetSmartUpdateEntitiesMutation,
  useLazyGetSmartUpdateCountQuery,
  useGetMappingViewQuery,
} = wizardCreateService;
